'use client';

import React, { useEffect } from 'react';
import type { Chat } from '../types/index.js';
import type { FilePart } from '../lib/db/types.js';
import type { AttachedSkill } from './skills/types.js';
import { useMessagesQuery } from '../hooks/api/index.js';
import { ChatLoadingState, ChatActiveChatState } from './ui/index.js';
import { ChatErrorRetry } from './ui/ChatErrorRetry.js';
import { useChatActivityOptional, type ChatRunStatus } from '../contexts/ChatActivityContext.js';

/**
 * One open chat tab's conversation, kept MOUNTED whether or not it is the tab
 * you are looking at.
 *
 * That is the whole point of tabs: an inactive pane still holds its `useChat`
 * instance, so a turn started before you switched away keeps streaming into
 * this component and is fully rendered the moment you switch back — no
 * remount, no history refetch, no lost scroll position.
 *
 * Hidden ≠ unmounted, and it is deliberately NOT `display: none`: the pane
 * stays absolutely positioned at full size so the virtualized message list
 * keeps real measurements. `invisible` + `pointer-events-none` + `inert` take
 * it out of sight, hit-testing, and the focus order.
 */

export interface ChatTabPaneProps {
  chatId: string;
  isActive: boolean;
  apiBaseUrl: string;
  locale: string;
  /** First-message seeds — only ever passed to the chat that was just started. */
  initialQuery: string;
  initialFiles: FilePart[];
  initialSkill: AttachedSkill | null;
  onMessageSent: () => void;
  onShare: (chatId: string) => void;
  onArchive: (chatId: string, chat: Chat) => void;
  onDelete: (chatId: string) => void;
  /** Reports the real title upward so the tab label stops saying "New chat". */
  onTitleResolved: (chatId: string, title: string) => void;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  meridianLogoUrl?: string;
  mcpConnectionsHref?: string;
  /** Deselects this chat when its loading state offers "back to list". */
  onBackToList: () => void;
}

export function ChatTabPane({
  chatId,
  isActive,
  apiBaseUrl,
  locale,
  initialQuery,
  initialFiles,
  initialSkill,
  onMessageSent,
  onShare,
  onArchive,
  onDelete,
  onTitleResolved,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  meridianLogoUrl,
  mcpConnectionsHref,
  onBackToList,
}: ChatTabPaneProps) {
  const activity = useChatActivityOptional();

  const messagesQuery = useMessagesQuery({
    apiBaseUrl,
    chatId,
    limit: 10,
    enabled: true,
  });

  const chat = messagesQuery.chat;
  const title = chat?.title;

  useEffect(() => {
    if (title) onTitleResolved(chatId, title);
  }, [chatId, title, onTitleResolved]);

  // A pane's status shadows the server poll for as long as it is mounted, so it
  // must retract that claim on unmount or a closed tab would look stuck.
  const clearChatStatus = activity?.clearChatStatus;
  useEffect(() => {
    return () => {
      clearChatStatus?.(chatId);
    };
  }, [chatId, clearChatStatus]);

  const reportChatStatus = activity?.reportChatStatus;
  const handleStatusChange = React.useCallback(
    (status: ChatRunStatus) => {
      reportChatStatus?.(chatId, status);
    },
    [chatId, reportChatStatus],
  );

  return (
    <div
      className={`absolute inset-0 flex flex-col ${
        isActive ? '' : 'invisible pointer-events-none'
      }`}
      // React 19 renders the real `inert` attribute, which removes the subtree
      // from the focus order and the a11y tree while it is off screen.
      inert={!isActive}
      aria-hidden={!isActive}
    >
      {messagesQuery.isLoading && !chat ? (
        <ChatLoadingState meridianLogoUrl={meridianLogoUrl} onBackToList={onBackToList} />
      ) : !chat && messagesQuery.isError ? (
        // A tab whose history failed to load would otherwise be a blank pane
        // with no way out — offer the retry in place (ERROR_STATES_ACTIONABLE).
        <div className="flex flex-1 items-center justify-center p-4">
          <ChatErrorRetry
            error={messagesQuery.error ?? undefined}
            onRetry={() => {
              void messagesQuery.refetch();
            }}
          />
        </div>
      ) : chat ? (
        <ChatActiveChatState
          selectedChatId={chatId}
          selectedChat={chat}
          apiBaseUrl={apiBaseUrl}
          locale={locale}
          initialQuery={initialQuery}
          initialFiles={initialFiles}
          initialSkill={initialSkill}
          hasMoreMessages={messagesQuery.hasNextPage}
          isLoadingOlderMessages={messagesQuery.isFetchingNextPage}
          messages={messagesQuery.data}
          onMessageSent={onMessageSent}
          onLoadOlderMessages={() => messagesQuery.fetchNextPage()}
          onShare={() => onShare(chatId)}
          onArchive={() => onArchive(chatId, chat)}
          onDelete={() => onDelete(chatId)}
          onStatusChange={handleStatusChange}
          botAvatarUrl={botAvatarUrl}
          userInitials={userInitials}
          userAvatarUrl={userAvatarUrl}
          meridianLogoUrl={meridianLogoUrl}
          mcpConnectionsHref={mcpConnectionsHref}
        />
      ) : null}
    </div>
  );
}
